import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { api } from '../services/api';
import { authApi } from '../services/auth-api';
import { useNotifications } from '../components/Notifications/NotificationProvider';

type UserRow = { id: string; email: string; username: string | null; role: 'READER' | 'AUTHOR' | 'MODERATOR'; reputation: number; banned?: boolean; bannedUntil?: string | null; createdAt: string };
type ReportRow = { id: string; targetType: 'TOPIC' | 'COMMENT'; targetId: string; reason: string | null; createdAt: string };
type ModQueueRow = { id: string; targetType: 'TOPIC' | 'COMMENT'; targetId: string; reason: string | null; createdAt: string; user: { id: string; email: string; username: string | null } };

const AdminPage: React.FC = () => {
  const { notify } = useNotifications();
  const [token, setToken] = useState<string | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [modQueue, setModQueue] = useState<ModQueueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [authStatus, setAuthStatus] = useState<'checking' | 'ok' | 'denied'>('checking');
  const [hardDeleteAvailable, setHardDeleteAvailable] = useState(false);
  const [confirm, setConfirm] = useState<{ open: boolean; type: 'local' | 'full'; user: UserRow | null; text: string; busy?: boolean; err?: string }>({ open: false, type: 'local', user: null, text: '' });
  const [banDurations, setBanDurations] = useState<Record<string, number>>({});
  const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 12px 28px rgba(15,23,42,0.08)', padding: 16, marginBottom: 20 };
  const softText = '#6b7280';
  const headerButton: React.CSSProperties = { padding: '9px 12px', borderRadius: 12, border: '1px solid #e5e7eb', background: 'rgba(255,255,255,0.9)', color: '#0f172a', fontWeight: 600, cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,0.06)' };
  const primaryButton: React.CSSProperties = { padding: '8px 12px', borderRadius: 10, border: '1px solid #0284c7', background: '#0284c7', color: '#fff', fontWeight: 700, cursor: 'pointer' };
  const ghostButton: React.CSSProperties = { padding: '7px 11px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#0f172a', fontWeight: 600, cursor: 'pointer' };
  const dangerButton: React.CSSProperties = { ...primaryButton, background: '#dc2626', borderColor: '#dc2626' };

  const rolePill = (role: UserRow['role']) => {
    const palette = {
      READER: { bg: '#e0f2fe', color: '#075985' },
      AUTHOR: { bg: '#ecfccb', color: '#365314' },
      MODERATOR: { bg: '#fee2e2', color: '#991b1b' }
    } as const;
    const { bg, color } = palette[role] || palette.READER;
    return <span style={{ background: bg, color, padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>{role}</span>;
  };

  const statusBadge = (label: string, tone: 'ok' | 'warn' | 'danger' = 'ok') => {
    const palette = {
      ok: { bg: '#ecfdf3', color: '#166534' },
      warn: { bg: '#fef9c3', color: '#854d0e' },
      danger: { bg: '#fee2e2', color: '#991b1b' }
    } as const;
    const { bg, color } = palette[tone];
    return <span style={{ background: bg, color, padding: '3px 9px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>{label}</span>;
  };

  useEffect(() => {
    const init = async () => {
      if (!supabase) return;
      const { data } = await supabase.auth.getSession();
      const tok = data.session?.access_token || null;
      setToken(tok);
      try {
        const user = await authApi.getCurrentUser();
        if (user?.role === 'MODERATOR') {
          setAuthStatus('ok');
        } else {
          setAuthStatus('denied');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setAuthStatus('denied');
        } else {
          setAuthStatus('denied');
          setError(err?.response?.data?.error || err?.message || 'Unable to verify moderator access.');
        }
      }
    };
    init();
  }, []);

  const requireAuth = () => {
    const redirect = encodeURIComponent(window.location.pathname);
    window.location.href = `/login?redirect=${redirect}`;
  };

  const loadUsers = useCallback(async () => {
    if (authStatus !== 'ok') return;
    try {
      setLoading(true); setError(null);
      const resp = await api.get('/admin/users');
      setUsers(resp.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load users');
    } finally { setLoading(false); }
  }, [authStatus]);

  const loadReports = useCallback(async () => {
    if (authStatus !== 'ok') return;
    try {
      setLoading(true); setError(null);
      const resp = await api.get('/admin/reports');
      setReports(resp.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load reports');
    } finally { setLoading(false); }
  }, [authStatus]);

  const loadModQueue = useCallback(async () => {
    if (authStatus !== 'ok') return;
    try {
      setLoading(true); setError(null);
      const resp = await api.get('/admin/modqueue');
      setModQueue(resp.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load moderation queue');
    } finally { setLoading(false); }
  }, [authStatus]);

  useEffect(() => {
    if (!token || authStatus !== 'ok') return;
    (async () => {
      try {
        const resp = await api.get('/admin/capabilities');
        setHardDeleteAvailable(Boolean(resp.data?.hardDelete));
      } catch { }
    })();
    loadUsers();
    loadReports();
    loadModQueue();
  }, [token, authStatus, loadUsers, loadReports, loadModQueue]);

  const setRole = async (id: string, role: 'READER' | 'AUTHOR' | 'MODERATOR') => {
    if (authStatus !== 'ok') return;
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      setUsers(u => u.map(x => x.id === id ? { ...x, role } : x));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to set role');
    }
  };

  const resolveReport = async (id: string) => {
    if (authStatus !== 'ok') return;
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      setReports(r => r.filter(x => x.id !== id));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to resolve report');
    }
  };

  const openConfirm = (type: 'local' | 'full', user: UserRow) => setConfirm({ open: true, type, user, text: '', busy: false });
  const closeConfirm = () => setConfirm({ open: false, type: 'local', user: null, text: '' });
  const performDelete = async () => {
    if (authStatus !== 'ok') return;
    const u = confirm.user; if (!u) return;
    if (confirm.text.trim() !== u.email.trim()) { setConfirm(c => ({ ...c, err: "Type the user's email to confirm" })); return; }
    setConfirm(c => ({ ...c, busy: true, err: undefined }));
    try {
      if (confirm.type === 'full') {
        await api.delete(`/admin/users/${u.id}`, { params: { hard: 1 } });
      } else {
        await api.delete(`/admin/users/${u.id}`);
      }
      setUsers(xs => xs.filter(x => x.id !== u.id));
      closeConfirm();
    } catch (e: any) {
      setConfirm(c => ({ ...c, busy: false, err: e?.response?.data?.error || e.message || 'Failed to delete user' }));
    }
  };

  const toggleBan = async (id: string, banned: boolean) => {
    if (authStatus !== 'ok') return;
    try {
      const durationDays = banDurations[id] ?? 7;
      const reason = !banned ? window.prompt('Reason for ban? (optional)')?.trim() || undefined : undefined;
      const payload = banned
        ? { banned: false }
        : { banned: true, durationMinutes: durationDays * 24 * 60, reason };
      const resp = await api.post(`/admin/users/${id}/ban`, payload);
      const { banned: newBanned, bannedUntil } = resp.data || {};
      setUsers(u => u.map(x => x.id === id ? { ...x, banned: newBanned, bannedUntil: bannedUntil || null } : x));
      const msg = newBanned
        ? `User banned${bannedUntil ? ` until ${new Date(bannedUntil).toLocaleString()}` : ''}`
        : 'User unbanned';
      notify({ type: 'success', message: msg });
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to update ban');
    }
  };

  const delTopic = async (id: string) => {
    if (authStatus !== 'ok') return;
    if (!window.confirm('Delete topic?')) return;
    try {
      await api.delete(`/admin/topics/${id}`);
      // Refresh lists to reflect auto-removed reports
      await loadReports();
      await loadModQueue();
    } catch (e: any) {
      notify({ type: 'error', message: 'Failed: ' + (e?.response?.data?.error || e.message) });
    }
  };
  const delComment = async (id: string) => {
    if (authStatus !== 'ok') return;
    if (!window.confirm('Delete comment?')) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      await loadReports();
      await loadModQueue();
    } catch (e: any) {
      notify({ type: 'error', message: 'Failed: ' + (e?.response?.data?.error || e.message) });
    }
  };

  const sortedUsers = useMemo(() => {
    const now = Date.now();
    return [...users].sort((a, b) => {
      const aBanned = !!a.banned || (!!a.bannedUntil && new Date(a.bannedUntil).getTime() > now);
      const bBanned = !!b.banned || (!!b.bannedUntil && new Date(b.bannedUntil).getTime() > now);
      if (aBanned !== bBanned) return aBanned ? -1 : 1;
      const aUntil = a.bannedUntil ? new Date(a.bannedUntil).getTime() : 0;
      const bUntil = b.bannedUntil ? new Date(b.bannedUntil).getTime() : 0;
      if (aUntil !== bUntil) return bUntil - aUntil;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return sortedUsers;
    return sortedUsers.filter((u) => {
      const haystack = [
        u.username,
        u.email,
        u.id
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [sortedUsers, userSearch]);

  const formatBanUntil = (u: UserRow) => {
    if (!u.banned && !u.bannedUntil) return '';
    if (u.bannedUntil) return new Date(u.bannedUntil).toLocaleString();
    return '—';
  };

  if (!supabase) {
    return (
      <div style={{ maxWidth: 900, margin: '32px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2>Admin</h2>
        <p>Supabase not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.</p>
      </div>
    );
  }

  if (authStatus === 'checking') {
    return (
      <div style={{ maxWidth: 900, margin: '48px auto', padding: '0 16px', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color: '#0f172a' }}>
        <h2>Moderator Console</h2>
        <p style={{ color: softText }}>Verifying access…</p>
      </div>
    );
  }

  if (authStatus === 'denied') {
    return (
      <div style={{ maxWidth: 640, margin: '64px auto', padding: '24px', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2 style={{ marginTop: 0 }}>Access denied</h2>
        <p style={{ color: softText, marginBottom: 16 }}>This page is restricted to moderators. Please sign in with a moderator account.</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={requireAuth} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #0284c7', background: '#0284c7', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Go to Sign In</button>
          <a href="/" style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#0f172a', fontWeight: 600, textDecoration: 'none' }}>Back to Home</a>
        </div>
        {error && <div style={{ marginTop: 12, color: '#b91c1c' }}>{error}</div>}
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ maxWidth: 600, margin: '32px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2>Moderator Console</h2>
        <p>You must sign in to view admin tools.</p>
        <button onClick={requireAuth} style={{ padding: '8px 12px' }}>Go to Sign In</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #0b1220 100%)', padding: '32px 0', fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div style={{ maxWidth: 1150, margin: '0 auto', padding: '0 20px' }}>
        <div style={{ background: 'linear-gradient(120deg,#0ea5e9,#22d3ee)', color: '#0b1220', padding: '20px 24px', borderRadius: 16, boxShadow: '0 16px 40px rgba(14,165,233,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>Moderation</div>
            <h2 style={{ margin: '4px 0 6px', fontSize: 26, fontWeight: 800 }}>Moderator Console</h2>
            <div style={{ color: '#0f172a', opacity: 0.85, fontWeight: 600 }}>Triage reports, manage users, and keep LoConvo healthy.</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button style={headerButton} onClick={loadReports} disabled={loading}>Sync data</button>
            <button style={headerButton} onClick={async () => { await supabase?.auth.signOut(); window.location.href = '/'; }}>Sign out</button>
            <a href="/" style={{ ...headerButton, textDecoration: 'none', background: '#0f172a', color: '#e2e8f0' }}>← Back</a>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
          <div style={{ ...cardStyle, marginBottom: 0, padding: '14px 16px', background: 'rgba(255,255,255,0.92)' }}>
            <div style={{ color: softText, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Users</div>
            <div style={{ fontSize: 26, fontWeight: 800 }}>{users.length}</div>
            <div style={{ color: softText, fontSize: 13 }}>accounts in scope</div>
          </div>
          <div style={{ ...cardStyle, marginBottom: 0, padding: '14px 16px', background: 'rgba(255,255,255,0.92)' }}>
            <div style={{ color: softText, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Open reports</div>
            <div style={{ fontSize: 26, fontWeight: 800 }}>{reports.length}</div>
            <div style={{ color: softText, fontSize: 13 }}>awaiting review</div>
          </div>
          <div style={{ ...cardStyle, marginBottom: 0, padding: '14px 16px', background: 'rgba(255,255,255,0.92)' }}>
            <div style={{ color: softText, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Mod queue</div>
            <div style={{ fontSize: 26, fontWeight: 800 }}>{modQueue.length}</div>
            <div style={{ color: softText, fontSize: 13 }}>items to triage</div>
          </div>
          <div style={{ ...cardStyle, marginBottom: 0, padding: '14px 16px', background: 'rgba(255,255,255,0.92)' }}>
            <div style={{ color: softText, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Hard delete</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{hardDeleteAvailable ? statusBadge('Enabled', 'warn') : statusBadge('Off')}</div>
            <div style={{ color: softText, fontSize: 13 }}>dangerous action gate</div>
          </div>
        </div>

        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 12, marginBottom: 16, border: '1px solid #fecdd3' }}>{error}</div>}

        <section style={{ marginBottom: 16 }}>
          <div style={{ ...cardStyle }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Users</h3>
                <p style={{ margin: '4px 0 0', color: softText, fontSize: 13 }}>Promote, ban, or remove accounts.</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  id="admin-user-search"
                  name="admin-user-search"
                  type="search"
                  placeholder="Search username or email"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    minWidth: 220
                  }}
                />
                <button style={primaryButton} onClick={loadUsers} disabled={loading}>Refresh</button>
              </div>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.6fr 2fr', padding: '10px 12px', fontWeight: 700, background: '#f8fafc', color: '#0f172a' }}>
                <div>User</div><div>Role</div><div>Reputation</div><div>Ban ends</div><div>Actions</div>
              </div>
              {filteredUsers.map(u => (
                <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.6fr 2fr', padding: '10px 12px', borderTop: '1px solid #e5e7eb', alignItems: 'center', background: u.banned ? 'rgba(254,242,242,0.6)' : '#fff' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{u.username || u.email}</div>
                    <div style={{ color: softText, fontSize: 12 }}>{u.email}</div>
                  </div>
                  <div>{rolePill(u.role)}</div>
                  <div style={{ fontWeight: 700 }}>{u.reputation}</div>
                  <div style={{ fontSize: 12, color: u.banned ? '#b91c1c' : softText }}>
                    {u.banned ? statusBadge('Banned', 'danger') : null} {formatBanUntil(u)}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={u.role} onChange={(e) => setRole(u.id, e.target.value as any)} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                      <option>READER</option>
                      <option>AUTHOR</option>
                      <option>MODERATOR</option>
                    </select>
                    {!u.banned && (
                      <select
                        value={banDurations[u.id] ?? 7}
                        onChange={(e) => setBanDurations(d => ({ ...d, [u.id]: parseInt(e.target.value, 10) || 7 }))}
                        style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                      >
                        <option value={1}>1 day</option>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={30}>30 days</option>
                      </select>
                    )}
                    <button style={ghostButton} onClick={() => toggleBan(u.id, !!u.banned)}>{u.banned ? 'Unban' : 'Ban'}</button>
                    <button style={ghostButton} onClick={() => openConfirm('local', u)}>Delete (local)</button>
                    {hardDeleteAvailable && (
                      <button style={dangerButton} onClick={() => openConfirm('full', u)}>Delete fully</button>
                    )}
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && <div style={{ padding: 12, color: softText }}>No users.</div>}
            </div>
          </div>
        </section>

        <section style={{ marginBottom: 16 }}>
          <div style={{ ...cardStyle }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Moderation Queue</h3>
                <p style={{ margin: '4px 0 0', color: softText, fontSize: 13 }}>Quick actions for fresh reports.</p>
              </div>
              <button style={primaryButton} onClick={loadModQueue} disabled={loading}>Refresh</button>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 2fr 1.5fr 2fr', padding: '10px 12px', fontWeight: 700, background: '#f8fafc' }}>
                <div>Type</div><div>Target</div><div>Reason</div><div>Reporter</div><div>Actions</div>
              </div>
              {modQueue.map((r) => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 2fr 1.5fr 2fr', padding: '10px 12px', borderTop: '1px solid #e5e7eb', alignItems: 'center', background: '#fff' }}>
                  <div>{statusBadge(r.targetType, r.targetType === 'TOPIC' ? 'warn' : 'ok')}</div>
                  <div style={{ fontFamily: 'monospace' }}>
                    {r.targetType === 'TOPIC' ? (
                      <a href={`/t/${r.targetId}`} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'none' }} onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'} onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}>{r.targetId.slice(0, 8)}…</a>
                    ) : (
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, color: '#0284c7', cursor: 'pointer', fontFamily: 'monospace', fontSize: 'inherit' }}
                        onClick={async () => {
                          try {
                            const resp = await api.get(`/comments/${r.targetId}`);
                            const topicId = resp.data?.topicId;
                            if (topicId) window.open(`/t/${topicId}?comment=${r.targetId}`, '_blank');
                            else notify({ type: 'warning', message: 'Could not determine topic for this comment' });
                          } catch (e: any) { notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to open' }); }
                        }}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                      >{r.targetId.slice(0, 8)}…</button>
                    )}
                  </div>
                  <div style={{ color: softText }}>{r.reason || '-'}</div>
                  <div style={{ fontWeight: 700 }}>{r.user.username || r.user.email}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={ghostButton} onClick={() => resolveReport(r.id)}>Resolve</button>
                    {r.targetType === 'TOPIC' ? (
                      <a style={{ ...ghostButton, textDecoration: 'none', display: 'inline-block' }} href={`/t/${r.targetId}`} target="_blank" rel="noreferrer">Open</a>
                    ) : (
                      <button style={ghostButton} onClick={async () => {
                        try {
                          const resp = await api.get(`/comments/${r.targetId}`);
                          const topicId = resp.data?.topicId;
                          if (topicId) window.open(`/t/${topicId}?comment=${r.targetId}`, '_blank');
                          else notify({ type: 'warning', message: 'Could not determine topic for this comment' });
                        } catch (e: any) { notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to open' }); }
                      }}>Open</button>
                    )}
                    <button style={ghostButton} onClick={() => { try { navigator.clipboard?.writeText(r.targetId); notify({ type: 'success', message: 'Copied target ID' }); } catch { } }}>Copy ID</button>
                  </div>
                </div>
              ))}
              {modQueue.length === 0 && <div style={{ padding: 12, color: softText }}>No items.</div>}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Reports</h3>
                <p style={{ margin: '4px 0 0', color: softText, fontSize: 13 }}>Full backlog of outstanding reports.</p>
              </div>
              <button style={primaryButton} onClick={loadReports} disabled={loading}>Refresh</button>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', padding: '10px 12px', fontWeight: 700, background: '#f8fafc' }}>
                <div>ID</div><div>Target</div><div>Reason</div><div>Actions</div>
              </div>
              {reports.map(r => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', padding: '10px 12px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>{r.id}</div>
                  <div>
                    {statusBadge(r.targetType, r.targetType === 'TOPIC' ? 'warn' : 'ok')}{' '}
                    {r.targetType === 'TOPIC' ? (
                      <a href={`/t/${r.targetId}`} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'none', fontFamily: 'monospace' }} onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'} onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}>· {r.targetId.slice(0, 8)}…</a>
                    ) : (
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, color: '#0284c7', cursor: 'pointer', fontFamily: 'monospace', fontSize: 'inherit' }}
                        onClick={async () => {
                          try {
                            const resp = await api.get(`/comments/${r.targetId}`);
                            const topicId = resp.data?.topicId;
                            if (topicId) window.open(`/t/${topicId}?comment=${r.targetId}`, '_blank');
                            else notify({ type: 'warning', message: 'Could not determine topic for this comment' });
                          } catch (e: any) { notify({ type: 'error', message: e?.response?.data?.error || e.message || 'Failed to open' }); }
                        }}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                      >· {r.targetId.slice(0, 8)}…</button>
                    )}
                  </div>
                  <div style={{ color: softText }}>{r.reason || '-'}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={ghostButton} onClick={() => resolveReport(r.id)}>Resolve</button>
                    {r.targetType === 'TOPIC' ? (
                      <button style={dangerButton} onClick={() => delTopic(r.targetId)}>Delete Topic</button>
                    ) : (
                      <button style={dangerButton} onClick={() => delComment(r.targetId)}>Delete Comment</button>
                    )}
                  </div>
                </div>
              ))}
              {reports.length === 0 && <div style={{ padding: 12, color: softText }}>No reports.</div>}
            </div>
          </div>
        </section>

        {confirm.open && confirm.user && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-card">
              <h4 style={{ margin: '4px 0 8px' }}>{confirm.type === 'full' ? 'Permanently delete user' : 'Delete user locally'}</h4>
              <p style={{ color: '#374151', fontSize: 14, margin: '0 0 8px' }}>
                {confirm.type === 'full' ? (
                  <>This removes the user from Supabase Auth and deletes all mirrored data in Neon. This cannot be undone.</>
                ) : (
                  <>This deletes the user and all their content from Neon only. If the user signs in again, they will be re-created.</>
                )}
              </p>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>Confirm by typing the user’s email:</p>
              <input
                type="text"
                placeholder={confirm.user.email}
                value={confirm.text}
                onChange={(e) => setConfirm(c => ({ ...c, text: e.target.value, err: undefined }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8 }}
              />
              {confirm.err && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>{confirm.err}</div>}
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeConfirm} disabled={confirm.busy}>Cancel</button>
                <button className={confirm.type === 'full' ? 'btn btn-danger' : 'btn btn-primary'} onClick={performDelete} disabled={confirm.busy}>
                  {confirm.busy ? 'Working…' : (confirm.type === 'full' ? 'Delete fully' : 'Delete local')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
